/*
  Analytics helpers — UTM tracking + Meta Pixel conversion events.
  Meta Pixel base script already loaded in public/index.html.
*/

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const LS_KEY = "fpai_utm";
const LS_LANDING = "fpai_landing";
const LS_REF = "fpai_referrer";

// Call once on app boot — captures UTM params from the URL and stores them
export function captureUTMs() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    let hasAny = false;
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) {
        captured[k] = v;
        hasAny = true;
      }
    });
    if (hasAny) {
      // Persist for 30 days
      const payload = { ...captured, captured_at: new Date().toISOString() };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
      // Server-side click log (one per tab session) → Admin → Metrics → "Ad funnel by campaign".
      // Then an engagement beacon at 5 s (and on leave) so junk clicks show up as bounces.
      if (!sessionStorage.getItem("fpai_visit_logged")) {
        sessionStorage.setItem("fpai_visit_logged", "1");
        const started = Date.now();
        const api = process.env.REACT_APP_BACKEND_URL;
        fetch(`${api}/api/public/visit`, {
          method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
          body: JSON.stringify({ ...captured, landing_path: window.location.pathname, referrer: document.referrer || "" }),
        })
          .then((r) => r.json())
          .then(({ id }) => {
            if (!id) return;
            try { sessionStorage.setItem("fpai_visit_id", id); } catch {}
            trackStep(window.location.pathname.startsWith("/pricing") ? "pricing" : window.location.pathname.startsWith("/signup") ? "signup_view" : "");
            const depth = () => {
              const h = document.documentElement.scrollHeight - window.innerHeight;
              return h > 0 ? Math.min(100, Math.round(((window.scrollY || 0) / h) * 100)) : 100;
            };
            const beacon = () => {
              const body = JSON.stringify({ seconds: Math.round((Date.now() - started) / 1000), depth: depth() });
              const url = `${api}/api/public/visit/${id}/engage`;
              if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
              else fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
            };
            setTimeout(beacon, 5200);
            window.addEventListener("pagehide", beacon);
            document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") beacon(); });
          })
          .catch(() => {});
      }
    }
    // Partner promo code (from /go/{code} redirects) — surfaced on the pricing page
    const promo = params.get("promo");
    if (promo && /^[A-Z0-9]{3,20}$/i.test(promo)) localStorage.setItem("fpai_promo", promo.toUpperCase());
    // Always record the landing page (helps attribution even without UTMs)
    if (!localStorage.getItem(LS_LANDING)) {
      localStorage.setItem(LS_LANDING, window.location.pathname + window.location.search);
    }
    // First external referrer (skip same-site navigations)
    if (!localStorage.getItem(LS_REF) && document.referrer && !document.referrer.includes(window.location.hostname)) {
      localStorage.setItem(LS_REF, document.referrer);
    }
  } catch (e) {
    // ignore
  }
}

// Merged attribution snapshot for lead submissions: current-URL UTMs win,
// stored first-touch UTMs fall back, plus landing path + external referrer.
// Signup-friction steps for ad visitors (Admin → Metrics → "Where ad visitors drop off").
// No-op for organic visitors (no ad-visit id in this tab).
export function trackStep(step) {
  try {
    const id = sessionStorage.getItem("fpai_visit_id");
    if (!id || !step) return;
    const key = `fpai_step_${step}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const body = JSON.stringify({ step });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/visit/${id}/step`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    else fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch {}
}

export function getAttribution() {
  const att = {};
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const stored = getStoredUTMs() || {};
    UTM_KEYS.forEach((k) => {
      const v = urlParams.get(k) || stored[k] || "";
      if (v) att[k] = v;
    });
    att.landing_path = getLandingPath() || "";
    att.referrer = localStorage.getItem(LS_REF) || "";
    att.visit_id = sessionStorage.getItem("fpai_visit_id") || "";
    const sv = localStorage.getItem("fpai_start_variant");
    if (sv === "clip" || sv === "form") att.start_variant = sv;
    const exps = {};
    Object.keys(localStorage).forEach((k) => { if (k.startsWith("fpai_exp_arm_")) exps[k.slice("fpai_exp_arm_".length)] = localStorage.getItem(k); });
    if (Object.keys(exps).length) att.experiments = exps;
  } catch {
    // ignore
  }
  return att;
}

// Experiments (A/B). Each browser is locked into one arm; a locked default (winner) overrides for everyone.
const EXP_DEFAULTS_KEY = "fpai_exp_defaults";
export function loadExperimentDefaults() {
  return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/experiments`)
    .then((r) => r.json())
    .then((d) => { const defs = d?.defaults || {}; try { sessionStorage.setItem(EXP_DEFAULTS_KEY, JSON.stringify(defs)); } catch {} return defs; })
    .catch(() => null);
}
export function cachedExperimentDefaults() {
  try { return JSON.parse(sessionStorage.getItem(EXP_DEFAULTS_KEY) || "null"); } catch { return null; }
}
export function getExperimentVariant(name, arms, { mobileOnly = false } = {}) {
  try {
    if (mobileOnly && window.innerWidth >= 1024) return null;
    const defs = cachedExperimentDefaults();
    if (defs && arms.includes(defs[name])) return { variant: defs[name], locked: true };
    const key = `fpai_exp_arm_${name}`;
    let v = localStorage.getItem(key);
    if (!arms.includes(v)) {
      v = arms[Math.floor(Math.random() * arms.length)];
      localStorage.setItem(key, v);
    }
    return { variant: v, locked: false };
  } catch {
    return null;
  }
}

// /start layout A/B — mobile only (desktop shows the same layout in both arms). Sticky per browser.
export function getStartVariant() {
  try {
    if (window.innerWidth >= 1024) return null;
    const defs = cachedExperimentDefaults();
    if (defs && (defs.start_layout === "clip" || defs.start_layout === "form")) return defs.start_layout;
    let v = localStorage.getItem("fpai_start_variant");
    if (v !== "clip" && v !== "form") {
      v = Math.random() < 0.5 ? "clip" : "form";
      localStorage.setItem("fpai_start_variant", v);
    }
    try { localStorage.setItem("fpai_exp_arm_start_layout", v); } catch {}
    return v;
  } catch {
    return null;
  }
}

export function reportExperiment(name, variant, attempt = 0) {
  if (!variant) return;
  try {
    const vid = sessionStorage.getItem("fpai_visit_id");
    if (!vid) {
      if (attempt < 10) setTimeout(() => reportExperiment(name, variant, attempt + 1), 600);
      return;
    }
    const key = `fpai_exp_${name}_${vid}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/visit/${vid}/experiment`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, variant }), keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

export function getStoredUTMs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredPromo() {
  try { return localStorage.getItem("fpai_promo") || ""; } catch { return ""; }
}

// Ad pixels — IDs are set by the admin at runtime (Partner Program → Ad pixels),
// so a pixel can be switched without a redeploy. Meta's base loader lives in
// index.html; the pixel is initialised here once we know which ID to use.
const DEFAULT_META_PIXEL_ID = "1556270059225623";

function initMetaPixel(id) {
  if (!id || typeof window.fbq !== "function" || window.__fpai_meta_pixel) return;
  window.__fpai_meta_pixel = id;
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

export async function loadPixels() {
  if (typeof window === "undefined" || window.__fpai_pixels_loaded) return;
  window.__fpai_pixels_loaded = true;
  let tiktokId = "";
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/pixels`);
    const data = await res.json();
    tiktokId = data.tiktok_pixel_id || "";
    initMetaPixel(data.meta_pixel_id || DEFAULT_META_PIXEL_ID);
  } catch {
    initMetaPixel(DEFAULT_META_PIXEL_ID);
  }
  try {
    const id = tiktokId;
    if (!id || window.ttq) return;
    /* eslint-disable */
    (function (w, d, t) {
      w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || []; ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) { var e = ttq._i[t] || []; for (var n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e; };
      ttq.load = function (e, n) { var r = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r; ttq._t = ttq._t || {}; ttq._t[e] = +new Date(); ttq._o = ttq._o || {}; ttq._o[e] = n || {}; var s = d.createElement("script"); s.type = "text/javascript"; s.async = true; s.src = r + "?sdkid=" + e + "&lib=" + t; var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(s, a); };
      ttq.load(id); ttq.page();
    })(window, document, "ttq");
    /* eslint-enable */
  } catch {
    // ignore — pixel is best-effort
  }
}

export function getLandingPath() {
  try {
    return localStorage.getItem(LS_LANDING) || null;
  } catch {
    return null;
  }
}

// Fire a Meta Pixel + GA4 + Google Ads conversion event.
// - Meta Pixel: standard event mapping (Lead, Schedule, Subscribe, etc.)
// - GA4: fires as a custom event under the configured measurement ID
// - Google Ads: fires as a `conversion` event with `send_to: {ADS_ID}/{LABEL}`
//   where LABEL comes from an event-specific env var. If no label is set,
//   Google Ads tracking silently no-ops for that event.
export function trackConversion(eventName, params = {}) {
  if (typeof window === "undefined") return;
  const utms = getStoredUTMs() || {};
  const enriched = { ...utms, ...params, landing_path: getLandingPath() };

  // Google Ads conversion labels — populate from Google Ads Conversions screen.
  // Set `REACT_APP_GOOGLE_ADS_ID=AW-1234567890` in frontend/.env, then set
  // one label env var per conversion (each has its own label in Google Ads).
  const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
  const ADS_LABELS = {
    signup_complete: process.env.REACT_APP_GADS_LABEL_SIGNUP || "",
    demo_book: process.env.REACT_APP_GADS_LABEL_DEMO || "",
    contact_submit: process.env.REACT_APP_GADS_LABEL_CONTACT || "",
    subscribe_complete: process.env.REACT_APP_GADS_LABEL_SUBSCRIBE || "",
    roi_submit: process.env.REACT_APP_GADS_LABEL_LEAD || "",
  };

  try {
    // Google Tag Manager — push every event to dataLayer so GTM can listen
    // and forward to GA4, Google Ads, or any other configured tag. GTM is
    // the primary event bus; gtag.js below is a redundant fallback.
    // GTM trigger recipe: "Custom Event" trigger with event name = `${eventName}`.
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...enriched });
    }

    // Meta Pixel — the fbq global is set by the Meta base script.
    // Pass eventID so the browser event dedupes with the server-side CAPI
    // event fired from the backend on the same conversion. Backend uses:
    //   signup    → event_id="signup_{user_id}"     matches Meta CompleteRegistration
    //   signup    → event_id="lead_{user_id}"       matches Meta Lead
    //   subscribe → event_id="subscribe_{sub_id}"   matches Meta Subscribe
    //   purchase  → event_id="purchase_{sess_id}"   matches Meta Purchase
    if (typeof window.fbq === "function") {
      const STANDARD_MAP = {
        signup_click: "Lead",
        signup_complete: "CompleteRegistration",
        card_added: "AddPaymentInfo",
        trial_start: "StartTrial",
        subscribe_complete: "Subscribe",
        demo_book: "Schedule",
        roi_submit: "Lead",
        contact_submit: "Contact",
        ai_playground_generate: "ViewContent",
        landing_view: "ViewContent",
      };
      const std = STANDARD_MAP[eventName];
      // Build a stable eventID for dedup. Frontend can't know the backend
      // user_id yet at signup-click time, so we use the browser event_id
      // hint from params.user_id if provided.
      const uid = params.user_id || params.external_id || "";
      const dedupIdByEvent = {
        signup_complete: uid ? `signup_${uid}` : undefined,
        signup_click:    uid ? `lead_${uid}`   : undefined,
        card_added: params.transaction_id ? `card_${params.transaction_id}` : undefined,
        trial_start: params.transaction_id ? `trial_${params.transaction_id}` : undefined,
        subscribe_complete: params.subscription_id ? `subscribe_${params.subscription_id}` : undefined,
      };
      const eventID = dedupIdByEvent[eventName];
      if (std) window.fbq("track", std, enriched, eventID ? { eventID } : undefined);
      window.fbq("trackCustom", eventName, enriched);
    }
    // TikTok Pixel (loaded at runtime by loadPixels when an ID is configured)
    if (window.ttq && typeof window.ttq.track === "function") {
      const TT_MAP = {
        signup_click: "ClickButton",
        signup_complete: "CompleteRegistration",
        card_added: "AddPaymentInfo",
        trial_start: "Subscribe",
        subscribe_complete: "Subscribe",
        demo_book: "Schedule",
        roi_submit: "SubmitForm",
        contact_submit: "Contact",
        landing_view: "ViewContent",
        checklist_view: "ViewContent",
      };
      const tt = TT_MAP[eventName];
      if (tt) window.ttq.track(tt, { content_name: eventName, value: params.value || 0, currency: params.currency || "USD" });
    }
    // Google Analytics (GA4) custom event
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, enriched);

      // Google Ads specific conversion — fires ONLY if we have both an
      // ADS_ID and a label for this event
      const label = ADS_LABELS[eventName];
      if (ADS_ID && label) {
        window.gtag("event", "conversion", {
          send_to: `${ADS_ID}/${label}`,
          value: params.value || 0,
          currency: params.currency || "USD",
          transaction_id: params.transaction_id || undefined,
        });
      }
    }
  } catch (e) {
    // silent
  }
}
